import { Table } from '@cureskin/api-client'
import { Component, OnDestroy, OnInit } from '@angular/core';
import {ActivatedRoute, Params, Router } from '@angular/router';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { OrderProductApprovalDetailObject } from '@cureskin/api-client/src/server-api';
import { TimeService } from '@services/time-service';
import { DeliveryDateService } from '@services/delivery-date-service/delivery-date.service';
import { AppConfig } from '../../app.config';
import { OrderService } from '@services/order-service/order.service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { Subscription } from 'rxjs';
import { Entity, EntityEnum } from '@cureskin/api-client-rn';

enum OrderTypeEnum {
  'completed',
  'active',
  'cancelled',
}

@Component({
  selector: 'user-order-list',
  templateUrl: './user-order-list.html',
  standalone: false,
})
export class UserOrderListComponent implements OnDestroy, OnInit {
  orderType: typeof OrderTypeEnum = OrderTypeEnum;
  activeOrderType: OrderTypeEnum = OrderTypeEnum.active;
  user: any;
  subscriptions: Array<Subscription> = [];
  orders: Array<Entity['Order']>;
  orderMap = new Map<string, {
    expectedDeliveryDate?: { from: Date; to: Date },
    cancelledProducts?: Entity['Order']['productApprovalDetails']['productDetails'];
    shouldShowProductCancellationUI?: boolean;
    shouldHideProductDetails?: boolean;
    products: Array<{
      product: Entity['Catalog'];
      image: string;
      quantity: number;
    }>;
    order: Entity['Order'];
  }>();
  isLoading: boolean = true;

  orderPaymentPendingStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderPaymentPendingStages];
  orderPlacedStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderPlacedStages];
  orderPackedStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderPackedStages];
  orderDispatchedStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderDispatchedStages];
  orderDeliveryFailedStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderDeliveryFailedStages];
  orderOutForDeliveryStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderOutForDeliveryStages];
  orderDeliveredStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderDeliveredStages];
  orderCanceledStages: Array<Entity['Order']['stage']> = [...this.appConfig.Shared.Order.Stage.orderCanceledStages];

  expectedDeliveryDateStages: Array<Entity['Order']['stage']> = [
    ...this.orderPlacedStages,
    ...this.orderPackedStages,
    ...this.orderDispatchedStages,
    ...this.orderDeliveryFailedStages,
    ...this.orderOutForDeliveryStages,
  ];

  freeProducts: Array<any>;
  deliveryIntervalInDays: number = 2;
  refundRelatedStageOrdersMap: any = new Map<string, any>();
  isEnglishOrHindiUser: boolean = false;
  cancelledProducts: Array<OrderProductApprovalDetailObject>;
  productApprovalDetails: Entity['Order']['productApprovalDetails'] ;
  experiments: any;
  availableFreeProductsFromExperiment: string[] = [];
  protected readonly STAGE = EntityEnum.Order.Stage;
  protected readonly PAYMENT_TYPE = EntityEnum.Order.PaymentType;
  constructor(
    private eventLogger: EventLoggerService,
    private broadcast: BroadcastService,
    private conn: ConnectionService,
    private route: ActivatedRoute,
    public appConfig: AppConfig,
    private appWebBridgeService: AppWebBridgeService,
    private router: Router,
    private readonly timeService: TimeService,
    private readonly deliveryDateService: DeliveryDateService,
    private readonly orderService: OrderService,
  ) {
    this.subscribeSubscriptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.map((each: Subscription): void => each.unsubscribe());
    this.subscriptions = [];
  }

  private subscribeSubscriptions(): void {
    this.subscriptions.push(this.route.queryParams.subscribe((params: Params): void => {
      this.appWebBridgeService.setStatusBarColor('yellow/accentLight');
    }));
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridgeService.setStatusBarColor('yellow/accentLight');
    this.user = await this.conn.getActingUser();
    this.getUserLanguagePreference();
    await this.getUserExperiments();
    this.activeOrderType = +(this.route.snapshot.queryParams?.tab || OrderTypeEnum.active);
    this.orderCanceledStages = this.orderCanceledStages.filter(
      (stage: string): boolean => !this.appConfig.Shared.Order.Stage.orderRefundStages.includes(stage));
    await this.loadOrders();
  }

  changeOrderType(orderType: OrderTypeEnum): void {
    this.activeOrderType = orderType;
    this.router.navigate([], { queryParams: { tab: orderType }, replaceUrl: true, relativeTo: this.route });
    this.loadOrders();
  }

  private async loadOrders(): Promise<void> {
    this.isLoading = true;
    let stages: Array<Entity['Order']['stage']>;
    switch (this.activeOrderType) {
      case OrderTypeEnum.active: {
        stages = [...EntityEnum.Order.OrderInActiveProcessStages, EntityEnum.Order.Stage.INITIAL, EntityEnum.Order.Stage.LOST_IN_TRANSIT];
        break;
      }
      case OrderTypeEnum.completed: {
        stages = [
          EntityEnum.Order.Stage.COMPLETED,
          EntityEnum.Order.Stage.DELIVERED,
        ]
        break;
      }
      case OrderTypeEnum.cancelled: {
        stages = [
          EntityEnum.Order.Stage.CANCELED,
          EntityEnum.Order.Stage.RETURNED,
          EntityEnum.Order.Stage.REFUND_PROCESSED,
          EntityEnum.Order.Stage.RETURN_INITIATED,
          EntityEnum.Order.Stage.RETURN_REQUESTED,
          EntityEnum.Order.Stage.REFUND_REQUESTED,
          EntityEnum.Order.Stage.REFUND_INITIATED,
          EntityEnum.Order.Stage.BOT_CANCELED,
        ]
        break;
      }
      default:
    }
    try {
      const orders = await this.conn.getOrders({ stages })
      this.orderMap.clear();
      this.refundRelatedStageOrdersMap.clear();
      this.checkForRefundStatus(orders);
      orders.forEach((order) => {
        const productQuantityMap: Record<string, {
          product: Entity['Catalog'];
          image: string;
          quantity: number;
        }> = {};
        order.products.forEach((product) => {
          if (productQuantityMap[product.objectId]) {
            productQuantityMap[product.objectId].quantity += 1;
            return;
          }
          productQuantityMap[product.objectId] = {
            product,
            quantity: 1,
            image: this.getProductImage(product),
          }
        });
        this.orderMap.set(order.objectId, { products: Object.values(productQuantityMap), order });
      });

      await Promise.all(
        orders.map(async (order: Entity['Order']): Promise<void> => {
          const parseOrder = new Table.Order();
          parseOrder.id = order.objectId;
          const orderPayments = await this.conn.findSuccessfulNonCODOrderPayments(parseOrder);
          await this.updateOrderAmountForCOD(order, orderPayments);
          this.setExpectedDeliveryDate(order);
          this.setOrderDisplayData(order);
        }),
      );
      this.orders = orders?.filter((order: Entity['Order']) => (order.stage !== EntityEnum.Order.Stage.INITIAL
        && order.type !== 'PRODUCT') || order.stage !== EntityEnum.Order.Stage.ONLINE_PAYMENT_PENDING);

      this.isLoading = false;
    } catch (error) {
      this.isLoading = false;
      this.eventLogger.logError(error);
      this.broadcast.broadcast('NOTIFY', { message: error.toString() });
    }
  }

  async setOrderDisplayData(order: Entity['Order']): Promise<void> {
    const existingOrderData = this.orderMap.get(order.objectId) || { products: [], order: {} };
    const shouldShowProductCancellationUI = this.orderService.shouldShowPartialOrderCancellationUI(order);
    const cancelledProducts = this.orderService.getCancelledProducts(order.productApprovalDetails);
    const updatedOrderData = {
      ...existingOrderData,
      shouldShowProductCancellationUI,
      cancelledProducts,
      shouldHideProductDetails: this.orderService.shouldHideProductDetails(order, cancelledProducts),
    };
    this.orderMap.set(order.objectId, updatedOrderData);
  }



  async updateOrderAmountForCOD(
    order: Entity['Order'] & { originalAmount?: number },
    orderPayments: Table.Payment[] = [],
  ): Promise<void> {
    const totalTransactionAmount = orderPayments?.reduce((total: number, payment: Table.Payment): number => {
      const transactionAmount = Number(payment?.get('TransactionAmount') || 0) / 100;
      return total + transactionAmount;
    }, 0) || 0;
    if (order?.paymentType === EntityEnum.Order.PaymentType.COD) {
       if (!order.originalAmount) {
         order.originalAmount = order.amount;
       }
      order.amount = order.originalAmount - totalTransactionAmount;
    }
  }

  getProductImage(product: Entity['Catalog']): string {
    let image = '';
    if (product?.rebrandedImageWithBackground?.length) {
      image = product.rebrandedImageWithBackground[product.rebrandedImageWithBackground.length - 1];
    } else if (product?.rebrandedImageWithoutBackground?.length) {
      image = product.rebrandedImageWithoutBackground[product.rebrandedImageWithoutBackground.length - 1];
    }
    return image;
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  getFreeProductDetails(order: Entity['Order']): number {
    const isRegimenOrder = order?.type === EntityEnum.Order.Type.REGIMEN;
    const regimenExtraProducts = (order?.regimenStateAtLastCheckpoint?.extraProducts
      || order?.regimen?.extraProducts);
    if (!regimenExtraProducts || !isRegimenOrder) return 0;

    this.freeProducts = order?.productInfo?.filter((product): boolean => {
      if (product.SP !== 0) return false;
      const hasFreeProductsInExperimentVariant = this.availableFreeProductsFromExperiment?.length > 0;
      return hasFreeProductsInExperimentVariant && this.availableFreeProductsFromExperiment.includes(product.id);
    });
    return this.freeProducts.length;
  }

  async setExpectedDeliveryDate(order: Entity['Order']): Promise<void> {
    const currentStage = order.stage;
    const isEligibleStage: boolean = [
      ...this.orderPlacedStages,
      ...this.orderPackedStages,
      ...this.orderDispatchedStages,
    ].includes(currentStage);
    if (!isEligibleStage) return;
    if (!this.isEnglishOrHindiUser) {
      const expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(null, this.deliveryIntervalInDays);
      const existingOrderData = this.orderMap.get(order.objectId) || { products: [], order: {} };
      this.orderMap.set(order.objectId, { ...existingOrderData, expectedDeliveryDate });
      return;
    }
    try {
      const res = await this.conn.getExpectedDeliveryDateByOrderId(order.objectId);
      const responseDateISO = res?.expectedDeliveryDate;
      const expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(
        responseDateISO,
        this.deliveryIntervalInDays,
      );
      const existingOrderData = this.orderMap.get(order.objectId) || { products: [], order: {} };
      this.orderMap.set(order.objectId, { ...existingOrderData, expectedDeliveryDate });
    } catch (error) {
      const expectedDeliveryDate = this.deliveryDateService.calculateExpectedDeliveryDate(
        new Date().toISOString(),
        this.deliveryIntervalInDays,
      );
      const existingOrderData = this.orderMap.get(order.objectId) || { products: [], order: {} };
      this.orderMap.set(order.objectId, { ...existingOrderData, expectedDeliveryDate });
    }
  }

  isCurrentOrderInRefundStages(orderId: string): boolean {
    return this.appConfig.Shared.Order.Stage.orderRefundStages.includes(this.refundRelatedStageOrdersMap.get(orderId)?.stage);
  }

  private hasExpectedDeliveryDate(order: Entity['Order']): boolean {
    return !!this.orderMap.get(order.objectId)?.expectedDeliveryDate?.to;
  }

  showDeliverOnDate(order: Entity['Order']): boolean {
    return !this.hasExpectedDeliveryDate(order) && !this.orderDeliveredStages.includes(order.stage) && !!order.deliverOnDate;
  }

  showDeliveredOn(order: Entity['Order']): boolean {
    return !this.hasExpectedDeliveryDate(order) && this.orderDeliveredStages.includes(order.stage) && !!order.deliveredOn;
  }

  showUserCancelled(order: Entity['Order']): boolean {
    return !this.isCurrentOrderInRefundStages(order.objectId) && this.orderCanceledStages.includes(order.stage) && !!order.canceledByUser;
  }

  async checkForRefundStatus(orders: Array<Entity['Order']>): Promise<void> {
    await Promise.all(
      orders.map(async (order: Entity['Order']): Promise<void> => {
        if (order.paymentType !== this.appConfig.Shared.Order.PaymentType.COD) {
          const latestOrderLog = await this.conn.fetchLatestOrderConsultationLog(order.objectId);
          if (latestOrderLog.stage) {
            this.refundRelatedStageOrdersMap.set(order.objectId, latestOrderLog);
          }
        }
      }),
    );
  }

  private getUserLanguagePreference(): void {
    this.isEnglishOrHindiUser = [this.appConfig.Shared.Languages.EN, this.appConfig.Shared.Languages.HI].includes(this.user?.get('languagePreference'));
  }

  async getUserExperiments(): Promise<void> {
    this.experiments = await this.conn.findUserActiveExperiments();
    for (const experiment of this.experiments) {
      if (experiment.key === 'add_extra_regimen_products') {
        this.availableFreeProductsFromExperiment = experiment.variant?.availableProducts || [];
      }
    }
  }
}
